import React from 'react';
import {
  Typography, Button, Box, TextField, Avatar, IconButton, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiFetch } from '../../apiFetch';
import { haptics } from '../../utils/haptics';
import { emptyGroceryFormData } from '../../types/Grocery';
import { AddToListStepProps } from './types';

const AddToListStep: React.FC<AddToListStepProps> = ({
  grocery,
  setGrocery,
  error,
  setError,
  loading,
  setLoading,
  onClose,
  isMobile,
  apiUrl,
  quantity,
  setQuantity,
  shoppingListId,
  setActiveStep,
  setWasAddedToList,
  setSelectedCatalog,
  setFormData,
  prices,
  setPrice,
  setIsDiscounted,
  setPriceBeforeDiscount,
}) => {
  const handleAddToList = async () => {
    if (!grocery) return;

    setLoading(true);
    setError(null);

    try {
      await apiFetch(`${apiUrl}/api/shopping-lists/${shoppingListId}/groceries/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grocery: grocery.id,
          quantity: quantity,
        }),
      });

      haptics.success();
      setWasAddedToList(true);

      const existingPrice = prices.get(grocery.id);
      if (existingPrice) {
        setPrice(existingPrice.price);
        setIsDiscounted(existingPrice.is_discounted);
        if (existingPrice.price_before_discount) {
          setPriceBeforeDiscount(existingPrice.price_before_discount);
        }
      }

      setActiveStep(2);
    } catch (err: any) {
      haptics.error();
      setError(err.message || 'Failed to add to shopping list');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipAddToList = () => {
    setWasAddedToList(false);

    if (grocery) {
      const existingPrice = prices.get(grocery.id);
      if (existingPrice) {
        setPrice(existingPrice.price);
        setIsDiscounted(existingPrice.is_discounted);
        if (existingPrice.price_before_discount) {
          setPriceBeforeDiscount(existingPrice.price_before_discount);
        }
      }
    }

    setActiveStep(2);
  };

  const handleBack = () => {
    setActiveStep(0);
    setGrocery(null);
    setSelectedCatalog(null);
    setFormData(emptyGroceryFormData);
  };

  if (!grocery) return null;

  return (
    <>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Add "{grocery.name}" to your shopping list?
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
        <Avatar
          src={grocery.image_icon || undefined}
          variant="rounded"
          sx={{ width: 60, height: 60 }}
        />
        <Box>
          <Typography variant="subtitle1">{grocery.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[grocery.brand, grocery.packaging_size].filter(Boolean).join(' | ')}
          </Typography>
        </Box>
      </Box>

      {/* Quantity selector with +/- buttons */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quantity
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            disabled={quantity <= 1}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <RemoveIcon />
          </IconButton>
          <TextField
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, style: { textAlign: 'center' } }}
            sx={{ width: 80 }}
            size="small"
          />
          <IconButton
            onClick={() => setQuantity(quantity + 1)}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {isMobile ? (
          <IconButton onClick={handleBack} disabled={loading}>
            <ArrowBackIcon />
          </IconButton>
        ) : (
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={loading}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={handleSkipAddToList} disabled={loading}>
            Skip
          </Button>
          <Button
            variant="contained"
            onClick={handleAddToList}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Add to List'}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AddToListStep;
